import { Card } from "./Card";
import { useLocale } from "./locale";
import type { LegalFramework } from "./api";

/** The law that governs this operator, by where it operates.
 *
 *  NestPlot's statutory lease/lien engine is US-only; offering a US operator's
 *  framework to a UK operator is worse than offering nothing — it tells a
 *  careful buyer we haven't thought about their jurisdiction. The card states
 *  the framework and what we do under it. It does NOT claim compliance on the
 *  operator's behalf, and says so: their obligations are theirs. The
 *  `statutory_engine` line is the honest one — outside the US it says the
 *  lease/lien automation does not apply.
 */
export function LegalFrameworkCard({ framework }: { framework: LegalFramework | null | undefined }) {
  const { t } = useLocale();
  if (!framework) return null;

  const Label = ({ children }: { children: React.ReactNode }) => (
    <div className="font-mono text-[10px] font-bold tracking-[0.16em] uppercase text-muted mb-1">
      {children}
    </div>
  );

  return (
    <Card>
      <div className="px-5 py-5">
        <div className="font-semibold text-ink mb-4">
          {t("legal.title")} · {framework.country_name}
        </div>

        <div className="space-y-4">
          <div>
            <Label>{t("legal.privacy")}</Label>
            <div className="text-sm text-ink leading-relaxed">{framework.privacy_law}</div>
            <div className="text-xs text-sub mt-1">
              {t("legal.regulator")}: {framework.privacy_regulator}
            </div>
          </div>

          <div>
            <Label>{t("legal.tenancy")}</Label>
            <div className="text-sm text-ink leading-relaxed">{framework.tenancy_law}</div>
          </div>

          <div>
            <Label>{t("legal.statutoryEngine")}</Label>
            <div className="text-sm text-ink leading-relaxed">{framework.statutory_engine}</div>
          </div>

          <div>
            <Label>{t("legal.agreement")}</Label>
            <div className="text-sm text-ink leading-relaxed">{framework.agreement}</div>
          </div>

          {framework.notes.map((n) => (
            <div key={n} className="text-xs text-sub leading-relaxed">
              {n}
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-line text-xs text-sub leading-relaxed">
          {t("legal.footer")}
        </div>
      </div>
    </Card>
  );
}
